import { format } from 'date-fns';
import { AlertCircle, ChevronRight, ExternalLink } from 'lucide-react';
import React from 'react';

import { ContactAttemptStatusBadge } from '@apps/web/components/outreaches/contact_attempt_status_badge';
import {
    getContactAttemptDeliveryModeLabel,
    isRelatedInboundTimelineItem,
} from '@apps/web/components/outreaches/outreach_detail';
import {
    getContactAttemptDetailsText,
    getOverallResponseFromShiftResponses,
} from '@apps/web/components/outreaches/utils';
import { VoiceCallRating } from '@apps/web/components/outreaches/voice_call_rating';
import { VoiceCallUserRatingDisplay } from '@apps/web/components/outreaches/voice_call_user_rating_display';
import { MessagesPane } from '@apps/web/components/shared/messages/messages_pane';
import { Badge } from '@apps/web/components/ui/badge';
import { Button } from '@apps/web/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@apps/web/components/ui/dialog';
import { ScrollArea } from '@apps/web/components/ui/scroll_area';
import {
    Tabs,
    TabsContent,
    TabsList,
    TabsTrigger,
} from '@apps/web/components/ui/tabs';
import { VoiceCallAudioPlayer } from '@apps/web/components/voice_calls/voice_call_audio_player';
import { useOrganizationTimezone } from '@apps/web/context/timezone_provider';
import { useOrganizationId } from '@apps/web/hooks/use_organization_id';
import { useDisplayNamesLastFirst } from '@apps/web/hooks/use_person_name_format';
import { formatCaregiverName } from '@apps/web/lib/caregiver_name';
import {
    generateExternalCaregiverUrl,
    getDataSourceDisplayName,
} from '@apps/web/lib/data_source_utils';
import { formatDate, formatTime } from '@apps/web/lib/date_utils';
import { cn } from '@apps/web/lib/utils';
import {
    useOrganizationConversations,
    useOrganizationDataSourceAccounts,
} from '@apps/web/swr_data_fetching/data_fetching_hooks';
import {
    type ContactAttempt,
    type OrgCaregiverRecord,
    type Shift,
} from '@libraries/typescript/api_client';

type Props = {
    readonly isOpen: boolean;
    readonly onClose: () => void;
    readonly caregiver: OrgCaregiverRecord;
    readonly attempts: Array<ContactAttempt>;
    readonly timelineItems?: Array<ContactAttempt>;
    readonly isAdmin?: boolean;
    readonly outreachId: string;
    readonly recordingUrls?: Record<string, string>;
    readonly shifts?: Array<Shift>;
    readonly wellskyOverrideReview?:
        | ContactAttempt['wellsky_override_review']
        | null;
    readonly onReviewOverride?: () => void;
    readonly skipBusyCaregivers?: boolean;
};

function formatWellSkyErrorTypeLabel(errorType: string): string {
    return errorType
        .split('_')
        .map((segment) =>
            segment.length > 0
                ? `${segment[0]!.toUpperCase()}${segment.slice(1)}`
                : segment,
        )
        .join(' ');
}

export function CaregiverOutreachDetailModal({
    isOpen,
    onClose,
    caregiver,
    attempts,
    timelineItems,
    isAdmin = false,
    outreachId,
    recordingUrls,
    shifts = [],
    wellskyOverrideReview,
    onReviewOverride,
    skipBusyCaregivers = false,
}: Props) {
    const organizationId = useOrganizationId();
    const { timezone } = useOrganizationTimezone();
    const displayCaregiverNamesLastFirst = useDisplayNamesLastFirst();
    const { data: dataSourceAccounts } = useOrganizationDataSourceAccounts(
        organizationId,
        {
            reason: 'caregiver-outreach-detail-modal',
        },
    );

    const timelineItemsForDisplay = timelineItems ?? attempts;
    const sortedAttempts = [...timelineItemsForDisplay].sort(
        (a, b) =>
            new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
    );
    const contactAttemptIdsRespondedViaInboundCall = React.useMemo(() => {
        const ids = new Set<string>();
        for (const item of timelineItemsForDisplay) {
            if (!isRelatedInboundTimelineItem(item)) {
                continue;
            }

            const details = item.communication_details as
                | Record<string, unknown>
                | null
                | undefined;
            if (!details) {
                continue;
            }

            const relatedContactAttemptIds =
                details['related_contact_attempt_ids'];
            if (!Array.isArray(relatedContactAttemptIds)) {
                continue;
            }

            for (const relatedContactAttemptId of relatedContactAttemptIds) {
                if (
                    typeof relatedContactAttemptId === 'string' &&
                    relatedContactAttemptId.length > 0
                ) {
                    ids.add(relatedContactAttemptId);
                }
            }
        }

        return ids;
    }, [timelineItemsForDisplay]);

    const smsAttempts = attempts.filter(
        (attempt) =>
            attempt.delivery_mode === 'sms' && attempt.conversation_id !== null,
    );
    const outreachConversationId =
        smsAttempts
            .map((attempt) => attempt.conversation_id)
            .find((id): id is string => id !== null) ?? null;
    const voiceAttemptForConversation = [...attempts]
        .filter(
            (attempt) =>
                attempt.delivery_mode === 'voice' &&
                attempt.voice_call?.direction === 'outbound' &&
                attempt.voice_call?.to_number &&
                attempt.voice_call.from_number,
        )
        .sort(
            (a, b) =>
                new Date(b.created_at).getTime() -
                new Date(a.created_at).getTime(),
        )[0];
    const contactPhoneNumber =
        voiceAttemptForConversation?.voice_call?.to_number ?? null;
    const outgoingPhoneNumber =
        voiceAttemptForConversation?.voice_call?.from_number ?? null;
    const { conversations: caregiverConversations } =
        useOrganizationConversations(
            organizationId,
            { reason: 'caregiver-outreach-detail-modal' },
            undefined,
            {
                caregiverId: caregiver.id,
                contactPhoneNumber: contactPhoneNumber ?? undefined,
                outgoingPhoneNumber: outgoingPhoneNumber ?? undefined,
                enabled:
                    isOpen &&
                    outreachConversationId === null &&
                    contactPhoneNumber !== null &&
                    outgoingPhoneNumber !== null,
                pageSize: 1,
            },
        );
    // Voice-only outreach has no SMS attempt to identify a thread. Resolve the
    // exact caregiver/Phoebe phone pair used by the call instead.
    const conversationId =
        outreachConversationId ?? caregiverConversations[0]?.id ?? null;
    const hasConversation = conversationId !== null;
    const hasWellSkyOverrideReview =
        wellskyOverrideReview?.type === 'wellsky_override_required';
    const initialTab = hasWellSkyOverrideReview
        ? 'override'
        : hasConversation
          ? 'messages'
          : 'timeline';
    const [activeTab, setActiveTab] = React.useState(initialTab);
    const tabsCount =
        1 + (hasConversation ? 1 : 0) + (hasWellSkyOverrideReview ? 1 : 0);
    const tabsGridClassName = tabsCount >= 3 ? 'grid-cols-3' : 'grid-cols-2';

    React.useEffect(() => {
        if (!isOpen) {
            return;
        }

        setActiveTab(initialTab);
    }, [caregiver.id, initialTab, isOpen]);

    const displayCaregiverName = formatCaregiverName(
        caregiver.name,
        displayCaregiverNamesLastFirst,
    );
    const caregiverSourceSystem = caregiver.source_system;
    const caregiverExternalId =
        caregiver.external_url_id ?? caregiver.external_id;
    const dataSourceAccount = caregiverSourceSystem
        ? dataSourceAccounts?.find(
              (account) => account.type === caregiverSourceSystem,
          )
        : null;
    const externalUrl =
        caregiverSourceSystem && caregiverExternalId && dataSourceAccount
            ? generateExternalCaregiverUrl(
                  caregiverSourceSystem,
                  caregiverExternalId,
                  dataSourceAccount.base_url,
              )
            : null;
    const systemName = caregiverSourceSystem
        ? getDataSourceDisplayName(caregiverSourceSystem)
        : null;

    type AttemptErrorDetails = {
        readonly provider: string | null;
        readonly errorType: string | null;
        readonly errorMessage: string | null;
        readonly httpStatus: number | null;
        readonly responseText: string | null;
    };

    const getErrorDetails = (
        attempt: ContactAttempt,
    ): AttemptErrorDetails | null => {
        if (attempt.outreach_status !== 'failed') {
            return null;
        }

        const details = attempt.communication_details as
            | Record<string, unknown>
            | null
            | undefined;

        if (!details) {
            return null;
        }

        // TODO: Make a real schema for this
        const providerValue = details['provider'];
        const provider =
            typeof providerValue === 'string' && providerValue.length > 0
                ? providerValue
                : null;

        const errorTypeValue = details['error_type'];
        const errorType =
            typeof errorTypeValue === 'string' && errorTypeValue.length > 0
                ? errorTypeValue
                : null;

        const errorMessageValue = details['error'];
        const errorMessage =
            typeof errorMessageValue === 'string' &&
            errorMessageValue.length > 0
                ? errorMessageValue
                : null;

        const httpStatusValue = details['http_status'];
        const httpStatus =
            typeof httpStatusValue === 'number' ? httpStatusValue : null;

        const responseTextValue = details['response_text'];
        const responseText =
            typeof responseTextValue === 'string' &&
            responseTextValue.length > 0
                ? responseTextValue
                : null;

        if (
            !provider &&
            !errorType &&
            !errorMessage &&
            !httpStatus &&
            !responseText
        ) {
            return null;
        }

        return {
            provider,
            errorType,
            errorMessage,
            httpStatus,
            responseText,
        };
    };

    const hasMultipleShifts = shifts.length > 1;
    const [expandedInboundTranscriptIds, setExpandedInboundTranscriptIds] =
        React.useState<Set<string>>(new Set());

    const getResponseBadgeClass = (response: string): string => {
        if (response === 'available_interested') {
            return 'border-green-500 text-green-700';
        }
        if (response === 'unavailable') {
            return 'border-red-500 text-red-700';
        }
        return 'border-yellow-500 text-yellow-700';
    };

    const getResponseBadgeDisplay = ({
        overallResponse,
        isResponseAttributedToInboundCall,
    }: {
        readonly overallResponse: string | null;
        readonly isResponseAttributedToInboundCall: boolean;
    }): { readonly label: string; readonly className: string } | null => {
        if (isResponseAttributedToInboundCall) {
            return {
                label: 'pending',
                className: getResponseBadgeClass('pending'),
            };
        }
        if (!overallResponse) {
            return null;
        }
        return {
            label: overallResponse.replace(/_/g, ' '),
            className: getResponseBadgeClass(overallResponse),
        };
    };

    const toggleInboundTranscript = React.useCallback((attemptId: string) => {
        setExpandedInboundTranscriptIds((previousValue) => {
            const nextValue = new Set(previousValue);
            if (nextValue.has(attemptId)) {
                nextValue.delete(attemptId);
            } else {
                nextValue.add(attemptId);
            }
            return nextValue;
        });
    }, []);

    const getTransferNumber = (attempt: ContactAttempt): string | null => {
        const callMetadata = attempt.voice_call?.call_metadata;
        if (!callMetadata || typeof callMetadata !== 'object') {
            return null;
        }

        const inboundMetadata = (callMetadata as Record<string, unknown>)[
            'inbound'
        ];
        if (!inboundMetadata || typeof inboundMetadata !== 'object') {
            return null;
        }

        const routingMetadata = (inboundMetadata as Record<string, unknown>)[
            'routing'
        ];
        if (!routingMetadata || typeof routingMetadata !== 'object') {
            return null;
        }

        const transferNumber = (routingMetadata as Record<string, unknown>)[
            'transfer_number'
        ];
        if (typeof transferNumber !== 'string' || transferNumber.length === 0) {
            return null;
        }

        return transferNumber;
    };

    const shouldShowTransferredBadge = (attempt: ContactAttempt): boolean =>
        attempt.outreach_status === 'transferred';

    const renderTranscriptLines = (transcriptText: string) => (
        <div className="space-y-2">
            {transcriptText
                .split('\n')
                .filter((line) => line.trim() !== '')
                .map((line, index) => {
                    const match = line.match(/^(Agent|User):\s*(.+)$/);
                    if (match) {
                        const [, speaker, message] = match;
                        return (
                            <div key={index} className="flex gap-3 text-sm">
                                <span className="min-w-[50px] font-medium">
                                    {speaker}:
                                </span>
                                <span className="flex-1">{message}</span>
                            </div>
                        );
                    }
                    return (
                        <div key={index} className="text-sm">
                            {line}
                        </div>
                    );
                })}
        </div>
    );

    if (!isOpen) {
        return null;
    }

    const renderShiftResponses = (
        shiftResponses: ContactAttempt['shift_responses'],
        options?: {
            readonly showPendingForMissingResponses?: boolean;
        },
    ) => {
        const showPendingForMissingResponses =
            options?.showPendingForMissingResponses ?? false;

        if (!shiftResponses && !showPendingForMissingResponses) {
            return null;
        }

        const normalizedShiftResponses = shiftResponses ?? {};
        const entries = Object.entries(normalizedShiftResponses);
        if (entries.length === 0 && !showPendingForMissingResponses) {
            return null;
        }

        // If single shift or no shifts info, show overall response
        if (!hasMultipleShifts) {
            const overallResponse = getOverallResponseFromShiftResponses(
                normalizedShiftResponses,
            );
            if (!overallResponse) {
                if (!showPendingForMissingResponses) {
                    return null;
                }
                return (
                    <Badge
                        variant="outline"
                        className={getResponseBadgeClass('pending')}
                    >
                        pending
                    </Badge>
                );
            }
            return (
                <Badge
                    variant="outline"
                    className={getResponseBadgeClass(overallResponse)}
                >
                    {overallResponse.replace(/_/g, ' ')}
                </Badge>
            );
        }

        // Multiple shifts - show per-shift responses in the order of the shifts array
        return (
            <div className="mt-2 space-y-1">
                {shifts.map((shift) => {
                    const response = normalizedShiftResponses[shift.id] ?? null;
                    if (response === null && !showPendingForMissingResponses) {
                        return null;
                    }

                    const displayResponse =
                        response?.replace(/_/g, ' ') ?? 'pending';
                    const badgeClass =
                        response === null
                            ? getResponseBadgeClass('pending')
                            : getResponseBadgeClass(response);
                    return (
                        <div
                            key={shift.id}
                            className="flex items-center gap-2 text-sm"
                        >
                            <span className="text-muted-foreground">
                                {shift.patient_name ?? 'Unknown shift'}:
                            </span>
                            <Badge variant="outline" className={badgeClass}>
                                {displayResponse}
                            </Badge>
                        </div>
                    );
                })}
            </div>
        );
    };

    const renderErrorDetails = (
        details: AttemptErrorDetails | null,
        showFullDetails: boolean,
    ) => {
        if (!details) {
            return null;
        }

        const { provider, errorType, errorMessage, httpStatus, responseText } =
            details;

        if (!showFullDetails) {
            if (!errorMessage) {
                return null;
            }

            return (
                <div className="rounded-md border border-red-200 bg-red-50 p-3 text-red-800">
                    <p className="text-xs break-words">{errorMessage}</p>
                </div>
            );
        }

        return (
            <div className="space-y-2 rounded-md border border-red-200 bg-red-50 p-3 text-red-800">
                <dl className="grid grid-cols-[max-content,1fr] gap-x-3 gap-y-1 text-xs">
                    {provider && (
                        <React.Fragment>
                            <dt className="font-medium">Provider</dt>
                            <dd className="break-words">{provider}</dd>
                        </React.Fragment>
                    )}
                    {errorType && (
                        <React.Fragment>
                            <dt className="font-medium">Error Type</dt>
                            <dd className="break-words">{errorType}</dd>
                        </React.Fragment>
                    )}
                    {errorMessage && (
                        <React.Fragment>
                            <dt className="font-medium">Message</dt>
                            <dd className="break-words">{errorMessage}</dd>
                        </React.Fragment>
                    )}
                    {typeof httpStatus === 'number' && (
                        <React.Fragment>
                            <dt className="font-medium">HTTP Status</dt>
                            <dd>{httpStatus}</dd>
                        </React.Fragment>
                    )}
                </dl>
                {responseText && (
                    <pre className="bg-background/80 max-h-40 overflow-auto rounded border border-red-200 p-2 text-xs whitespace-pre-wrap text-red-900 dark:border-red-900 dark:text-red-300">
                        {responseText}
                    </pre>
                )}
            </div>
        );
    };

    const renderTimelineAttemptCard = (attempt: ContactAttempt) => {
        const errorDetails = getErrorDetails(attempt);
        const attemptShiftResponses = attempt.shift_responses ?? null;
        const isInboundCall = isRelatedInboundTimelineItem(attempt);
        const overallResponse = getOverallResponseFromShiftResponses(
            attemptShiftResponses,
        );
        const isResponseAttributedToInboundCall =
            !isInboundCall &&
            contactAttemptIdsRespondedViaInboundCall.has(attempt.id);
        const responseBadgeDisplay = getResponseBadgeDisplay({
            overallResponse,
            isResponseAttributedToInboundCall,
        });
        const transferNumber = getTransferNumber(attempt);
        const isTransferred = shouldShowTransferredBadge(attempt);
        const isTranscriptExpanded =
            !isInboundCall || expandedInboundTranscriptIds.has(attempt.id);

        return (
            <div key={attempt.id} className="space-y-3 rounded-lg border p-4">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <Badge variant="secondary">
                            {getContactAttemptDeliveryModeLabel(attempt)}
                        </Badge>
                        <ContactAttemptStatusBadge
                            status={attempt.outreach_status}
                            deliveryMode={attempt.delivery_mode}
                            isInboundCall={isInboundCall}
                            communicationDetails={attempt.communication_details}
                            currentShiftEndTime={attempt.current_shift_end}
                            showBusyQueuedState={skipBusyCaregivers}
                            voiceCallStartedAt={attempt.voice_call?.started_at}
                            timezone={timezone}
                        />
                        {!hasMultipleShifts &&
                            !isInboundCall &&
                            responseBadgeDisplay && (
                                <Badge
                                    variant="outline"
                                    className={responseBadgeDisplay.className}
                                >
                                    {responseBadgeDisplay.label}
                                </Badge>
                            )}
                        {isTransferred && (
                            <Badge variant="outline">Transferred</Badge>
                        )}
                    </div>
                    <p className="text-muted-foreground text-sm">
                        {format(
                            new Date(attempt.created_at),
                            'MMM d, yyyy, h:mm a',
                        )}
                    </p>
                </div>
                {hasMultipleShifts &&
                    !isInboundCall &&
                    renderShiftResponses(
                        isResponseAttributedToInboundCall
                            ? {}
                            : (attemptShiftResponses ?? {}),
                        isResponseAttributedToInboundCall
                            ? {
                                  showPendingForMissingResponses: true,
                              }
                            : undefined,
                    )}
                {renderErrorDetails(errorDetails, isAdmin)}
                {transferNumber && (
                    <p className="text-muted-foreground text-sm">
                        {isTransferred
                            ? 'Transferred to '
                            : 'Transfer target: '}
                        <span className="font-mono text-xs">
                            {transferNumber}
                        </span>
                    </p>
                )}
                {attempt.voice_call && (
                    <div>
                        <VoiceCallAudioPlayer
                            voiceCall={attempt.voice_call}
                            prefetchedRecordingUrl={
                                recordingUrls?.[attempt.voice_call.id]
                            }
                            className="h-10 w-full focus:outline-none"
                        />
                    </div>
                )}
                {attempt.call_summary && (
                    <div className="mb-3 rounded-md bg-blue-50 p-3 dark:bg-blue-950/30">
                        <p className="mb-2 text-sm font-medium">Call Summary</p>
                        <p className="text-foreground/80 text-sm">
                            {attempt.call_summary}
                        </p>
                    </div>
                )}
                {attempt.transcript_text && (
                    <div className="bg-muted/50 rounded-md p-3">
                        {isInboundCall ? (
                            <button
                                type="button"
                                onClick={() =>
                                    toggleInboundTranscript(attempt.id)
                                }
                                className="hover:text-foreground inline-flex items-center gap-1 text-sm font-medium"
                            >
                                <ChevronRight
                                    className={cn(
                                        'h-4 w-4 transition-transform',
                                        isTranscriptExpanded
                                            ? 'rotate-90'
                                            : 'rotate-0',
                                    )}
                                />
                                Transcript
                            </button>
                        ) : (
                            <p className="mb-2 text-sm font-medium">
                                Transcript
                            </p>
                        )}
                        {isTranscriptExpanded &&
                            renderTranscriptLines(attempt.transcript_text)}
                    </div>
                )}
                {!attempt.transcript_text && (
                    <p className="text-muted-foreground text-sm italic">
                        {getContactAttemptDetailsText(attempt)}
                    </p>
                )}
                {attempt.voice_call && (
                    <div className="mt-3 space-y-3 border-t pt-3">
                        <VoiceCallUserRatingDisplay
                            voiceCallId={attempt.voice_call.id}
                        />
                        <VoiceCallRating voiceCallId={attempt.voice_call.id} />
                    </div>
                )}
            </div>
        );
    };

    const renderTimelineSection = ({
        withTopBorder,
    }: {
        readonly withTopBorder: boolean;
    }) => (
        <div
            className={cn(
                'flex min-h-0 flex-1 flex-col',
                withTopBorder && 'border-t pt-6',
            )}
        >
            <h3 className="mb-4 flex-shrink-0 text-lg font-semibold">
                Communication Timeline
            </h3>
            <ScrollArea className="min-h-0 flex-1">
                <div className="space-y-4 pr-4">
                    {sortedAttempts.map(renderTimelineAttemptCard)}
                </div>
            </ScrollArea>
        </div>
    );

    const renderOverrideReviewSection = ({
        withTopBorder,
    }: {
        readonly withTopBorder: boolean;
    }) => {
        if (!hasWellSkyOverrideReview) {
            return null;
        }

        return (
            <div
                className={cn(
                    'flex min-h-0 flex-1 flex-col',
                    withTopBorder && 'border-t pt-6',
                )}
            >
                <div className="mb-4 flex-shrink-0 space-y-4">
                    <div>
                        <h3 className="text-lg font-semibold">
                            Override Review
                        </h3>
                        <p className="text-muted-foreground text-sm">
                            Coordinator approval is required before WellSky
                            overrides are applied.
                        </p>
                    </div>
                    <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
                        <div className="flex items-start gap-2">
                            <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-600" />
                            <div className="space-y-1">
                                <p className="font-medium text-amber-800">
                                    WellSky Assignment Warning
                                </p>
                                <p className="text-sm text-amber-700">
                                    Review the stored WellSky warnings, then
                                    explicitly accept any overrides you want to
                                    apply.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
                <ScrollArea className="min-h-0 flex-1">
                    <div className="space-y-4 pr-4">
                        {wellskyOverrideReview.shift_reviews.map(
                            (shiftReview) => {
                                const reviewedShift = shifts.find(
                                    (shift) =>
                                        shift.id === shiftReview.shift_id,
                                );
                                const shiftLabel = reviewedShift
                                    ? `${reviewedShift.patient_name ?? 'Selected shift'} • ${formatDate(reviewedShift.shift_start_time, timezone)} • ${formatTime(reviewedShift.shift_start_time, timezone)}-${formatTime(reviewedShift.shift_end_time, timezone)}`
                                    : 'Selected shift';

                                return (
                                    <div
                                        key={shiftReview.shift_id}
                                        className="bg-background rounded-lg border border-amber-200 p-4 dark:border-amber-900"
                                    >
                                        <p className="text-sm font-medium text-amber-800 dark:text-amber-300">
                                            {shiftLabel}
                                        </p>
                                        <div className="mt-3 space-y-2">
                                            {shiftReview.wellsky_errors.errors.map(
                                                (error, index) => (
                                                    <div
                                                        key={`${shiftReview.shift_id}-${index}`}
                                                        className="rounded border border-amber-100 bg-amber-50/40 p-3"
                                                    >
                                                        <p className="text-sm font-medium text-amber-800">
                                                            {formatWellSkyErrorTypeLabel(
                                                                error.error_type,
                                                            )}
                                                        </p>
                                                        <p className="mt-1 text-xs text-amber-700">
                                                            {error.message}
                                                        </p>
                                                        {error.hours_exceeded && (
                                                            <p className="mt-1 text-xs text-amber-600">
                                                                Hours exceeded:{' '}
                                                                {
                                                                    error.hours_exceeded
                                                                }
                                                            </p>
                                                        )}
                                                    </div>
                                                ),
                                            )}
                                        </div>
                                    </div>
                                );
                            },
                        )}
                    </div>
                </ScrollArea>
                {onReviewOverride && (
                    <div className="mt-4 flex flex-shrink-0 justify-end border-t pt-4">
                        <Button onClick={onReviewOverride}>
                            Review override
                        </Button>
                    </div>
                )}
            </div>
        );
    };

    return (
        <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
            <DialogContent
                data-testid="caregiver-outreach-detail-dialog-content"
                className="flex h-[90vh] max-h-[900px] flex-col overflow-hidden max-sm:fixed max-sm:inset-0 max-sm:h-[100dvh] max-sm:max-h-[100dvh] max-sm:w-[100dvw] max-sm:max-w-[100dvw] max-sm:translate-x-0 max-sm:translate-y-0 max-sm:overflow-hidden max-sm:rounded-none max-sm:border-0 max-sm:p-4 max-sm:pt-[calc(1rem+env(safe-area-inset-top))] sm:max-w-[64vw] max-sm:[&_[data-slot=dialog-close]]:top-[calc(0.75rem+env(safe-area-inset-top))] max-sm:[&_[data-slot=dialog-close]]:right-3"
            >
                <div className="flex flex-shrink-0 items-start justify-between gap-3 max-sm:min-w-0 max-sm:pr-10">
                    <DialogHeader className="min-w-0 flex-1 text-left">
                        <DialogTitle className="truncate">
                            {displayCaregiverName}
                        </DialogTitle>
                        <DialogDescription>
                            Outreach details and communication history
                        </DialogDescription>
                    </DialogHeader>
                    <div className="mr-6 flex flex-col items-end gap-2 text-right max-sm:mr-0 max-sm:shrink-0">
                        {externalUrl && systemName && (
                            <Button variant="outline" size="sm" asChild={true}>
                                <a
                                    href={externalUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    <ExternalLink className="mr-1 h-4 w-4" />
                                    View in {systemName}
                                </a>
                            </Button>
                        )}
                        <div>
                            <p className="text-muted-foreground text-sm font-medium">
                                Total Attempts
                            </p>
                            <p className="text-2xl font-semibold">
                                {attempts.length}
                            </p>
                        </div>
                    </div>
                </div>
                {tabsCount > 1 ? (
                    <Tabs
                        value={activeTab}
                        onValueChange={setActiveTab}
                        className="flex min-h-0 flex-1 flex-col overflow-hidden"
                    >
                        <TabsList
                            className={cn(
                                'grid w-full flex-shrink-0',
                                tabsGridClassName,
                            )}
                        >
                            <TabsTrigger value="timeline">Timeline</TabsTrigger>
                            {hasConversation && (
                                <TabsTrigger value="messages">
                                    SMS Conversation
                                </TabsTrigger>
                            )}
                            {hasWellSkyOverrideReview && (
                                <TabsTrigger value="override">
                                    Override Review
                                </TabsTrigger>
                            )}
                        </TabsList>
                        <TabsContent
                            value="timeline"
                            className="min-h-0 flex-1 overflow-hidden data-[state=active]:flex data-[state=active]:flex-col"
                        >
                            {renderTimelineSection({
                                withTopBorder: false,
                            })}
                        </TabsContent>
                        {hasConversation && (
                            <TabsContent
                                value="messages"
                                className="min-h-0 flex-1 overflow-hidden data-[state=active]:flex data-[state=active]:flex-col"
                            >
                                {/* Not scoped to this outreach: the tab shows
                                    the whole thread, so it shows every pending
                                    reply in it, the same as Conversations. A
                                    reply is only tagged with an outreach when
                                    the agent recorded a response, so scoping
                                    hid ordinary pending replies. */}
                                <MessagesPane
                                    timezone={timezone}
                                    organizationId={organizationId}
                                    conversationId={conversationId}
                                    refreshOnOpen={activeTab === 'messages'}
                                    isActive={activeTab === 'messages'}
                                    showComposer={true}
                                />
                            </TabsContent>
                        )}
                        {hasWellSkyOverrideReview && (
                            <TabsContent
                                value="override"
                                className="min-h-0 flex-1 overflow-hidden data-[state=active]:flex data-[state=active]:flex-col"
                            >
                                {renderOverrideReviewSection({
                                    withTopBorder: false,
                                })}
                            </TabsContent>
                        )}
                    </Tabs>
                ) : (
                    renderTimelineSection({ withTopBorder: true })
                )}
            </DialogContent>
        </Dialog>
    );
}
